import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from 'recharts';

interface MoodRadarProps {
  mood: Record<string, number>;
  rationale?: string;
}

const DIMENSIONS = [
  { key: 'energy', label: 'Energy', low: 'Drained', high: 'Animated' },
  { key: 'warmth', label: 'Warmth', low: 'Distant', high: 'Intimate' },
  { key: 'tension', label: 'Tension', low: 'Relaxed', high: 'Urgent' },
  { key: 'focus', label: 'Focus', low: 'Scattered', high: 'Concentrated' },
  { key: 'openness', label: 'Openness', low: 'Guarded', high: 'Vulnerable' },
];

const DIMENSION_COLORS: Record<string, string> = {
  energy: 'bg-amber-400',
  warmth: 'bg-rose-400',
  focus: 'bg-blue-400',
  openness: 'bg-violet-400',
};

function getTensionColor(value: number): string {
  if (value > 0.6) return 'bg-red-400';
  if (value > 0.3) return 'bg-orange-400';
  return 'bg-emerald-400';
}

export function MoodRadar({ mood, rationale }: MoodRadarProps) {
  const chartData = DIMENSIONS.map((d) => ({
    dimension: d.label,
    value: (mood[d.key] ?? 0) * 100,
    fullMark: 100,
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Radar Chart */}
        <div className="w-[280px] h-[220px] flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid stroke="#4a4a4a" />
              <PolarAngleAxis dataKey="dimension" tick={{ fill: '#a0a0a0', fontSize: 11 }} />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fill: '#707070', fontSize: 9 }}
                tickCount={5}
              />
              <Radar
                name="Mood"
                dataKey="value"
                stroke="#d4a574"
                fill="#d4a574"
                fillOpacity={0.4}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Dimension Bars */}
        <div className="flex-1 space-y-3 w-full">
          {DIMENSIONS.map((d) => {
            const value = mood[d.key] ?? 0;
            const colorClass =
              d.key === 'tension' ? getTensionColor(value) : DIMENSION_COLORS[d.key];

            return (
              <div key={d.key} className="flex items-center gap-2">
                <span className="w-16 text-xs text-parchment">{d.label}</span>
                <span className="w-20 text-[10px] text-mist/50 text-right">{d.low}</span>
                <div className="flex-1 h-2 bg-stone/30 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${colorClass}`}
                    style={{ width: `${value * 100}%` }}
                  />
                </div>
                <span className="w-20 text-[10px] text-mist/50">{d.high}</span>
                <span className="w-8 text-xs text-mist/80 text-right">
                  {(value * 100).toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {rationale && (
        <div className="pt-3 border-t border-stone/20">
          <div className="text-xs text-mist/70 italic">{rationale}</div>
        </div>
      )}
    </div>
  );
}
